import { BsdsCard } from '@bsds-lib-component/react';
import React from 'react';

import type { ListaSubestipulanteItem } from '../../../types/vida/subEstipulante/subEstipulanteType';

const CARD_BASE_CLASS =
  'min-h-60 w-full p-6 border border-gray-100 shadow-sm transition-all duration-200 hover:shadow-md hover:shadow-lg';

type LabeledValueProps = {
  label: string;
  value: string;
  highlight?: boolean;
};

const LabeledValue: React.FC<LabeledValueProps> = ({ label, value, highlight }) => (
  <div className="flex flex-1 min-w-0 flex-col">
    <div className="bsds-mt-4 bsds-p-4 bg-gray-50/20 rounded-md border border-gray-100 flex flex-col bsds-gap-8">
      <span className="text-sm text-gray-300 font-small">{label}</span>
      <span
        className={
          highlight ? 'text-sm font-bold text-primary-700' : 'text-sm font-semibold text-gray-900'
        }
      >
        {value}
      </span>
    </div>
  </div>
);

function resolveStatusBadgeClass(status: string): string {
  const normalized = status
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  if (normalized.includes('ativo') || normalized.includes('vigent')) {
    return 'bg-green-300 text-white';
  }
  if (normalized.includes('cancel') || normalized.includes('inativ')) {
    return 'bg-red-200 text-white';
  }
  return 'bg-gray-200 text-gray-700';
}

export type HomeSubestipulanteCardProps = {
  data: ListaSubestipulanteItem;
  formatDocumento: (raw: string | undefined) => string;
  formatVigencia: (value: string | undefined) => string;
};

const HomeSubestipulanteCard: React.FC<HomeSubestipulanteCardProps> = ({
  data,
  formatDocumento,
  formatVigencia,
}) => {
  const nome = data.nomeSubestipulanteOut?.trim() || '—';
  const status = data.dsCstatus?.trim() || '—';
  const statusBadgeClass = resolveStatusBadgeClass(status);

  const ariaSummary = [
    `Subestipulante ${nome}`,
    `Contrato ${data.identContratoOut ?? '—'}`,
    ` Documento ${formatDocumento(data.numDocumentoOut)}`,
    `Status ${status}`,
  ].join(', ');

  return (
    <BsdsCard className={CARD_BASE_CLASS} platform="web" role="listitem" aria-label={ariaSummary}>
      <div className="bsds-flex-direction-column bsds-align-items-start bsds-width-100">
        <div className="bsds-flex-direction-column bsds-width-100 bsds-mb-12 bsds-gap-4">
          <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-start bsds-width-100 bsds-gap-4">
            <span className="text-lg font-semibold text-gray-900 text-start">{nome}</span>
            <BsdsCard
              platform="web"
              className={`px-3 py-1 rounded-md border-0 shadow-none shrink-0 ${statusBadgeClass}`}
            >
              <span className="text-sm font-semibold text-right">{status}</span>
            </BsdsCard>
          </div>

          <div className="bsds-display-flex bsds-flex-direction-row bsds-justify-content-between bsds-align-items-center bsds-width-100 bsds-gap-8 bsds-mt-12">
            <span className="text-sm text-gray-300 text-start shrink-0">Vigência</span>
            <div className="bsds-display-flex bsds-flex-direction-row bsds-align-items-center bsds-gap-8">
              <span className="text-sm text-gray-500 text-start">{formatVigencia(data.dataIniVigenciaOut)}</span>
              <span className="text-sm text-gray-500 text-start bsds-ml-4 bsds-mr-4">a</span>
              <span className="text-sm text-gray-500 text-start">{formatVigencia(data.dataFimVigenciaOut)}</span>
            </div>
          </div>
        </div>

        <hr className="bsds-width-100 border-0 border-t border-gray-200" aria-hidden="true" />

        <div className="grid grid-cols-1 gap-2 bsds-width-100 bsds-mt-8">
          <LabeledValue label="Contrato" value={String(data.identContratoOut ?? '—')} />
          <LabeledValue label="Número do Documento" value={formatDocumento(data.numDocumentoOut)} />
        </div>
      </div>
    </BsdsCard>
  );
};

export default HomeSubestipulanteCard;
